import React, { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/InfraAutomation")({
  component: InfraAutomation,
});

type Tab = "AGGREGATION_ENGINE" | "DATA_NORMALIZATION" | "SOURCE_HEALTH";

function InfraAutomation() {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<Tab>("AGGREGATION_ENGINE");
  const [isProcessing, setIsProcessing] = useState(false);
  const [itemsAggregated, setItemsAggregated] = useState(1420);
  const [progress, setProgress] = useState(100);
  const [logs, setLogs] = useState<string[]>([
    "[READY] YouTube Data API v3 Connected",
    "[READY] OpenPyXL Engine: Master Template Loaded",
    "[IDLE] Waiting for input directory sync..."
  ]);

  const sources = [
    { name: "YouTube API", type: "Video Metadata", status: "CONNECTED", items: "450 Titles" },
    { name: "Inventory_SKUs.xlsx", type: "Excel Source", status: "READING", items: "1,200 SKUs" },
    { name: "Marketing_Links.csv", type: "URL Source", status: "STABLE", items: "890 Links" },
  ];

  const triggerAutomation = () => {
    setIsProcessing(true);
    setProgress(0);
    const newLogs = [
      "[PY] Scraping YouTube video titles and shortlinks...",
      "[PY] Cross-referencing SKU numbers from Excel Manifests...",
      "[PY] Generating custom URL slugs for E-commerce template..."
    ];
    setLogs(prev => [...newLogs, ...prev].slice(0, 8));

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);
          setItemsAggregated(i => i + 124);
          setLogs(prevLogs => ["[SUCCESS] Master_Upload_Template.xlsx generated.", ...prevLogs]);
          return 100;
        }
        return prev + 25;
      });
    }, 500);
  };

  return (
    <div style={containerStyle}>
      <nav style={navStyle}>
        <div style={navBrand} onClick={() => navigate({ to: "/Portfolio" })} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={avatarStyle}>JF</div>
          <span style={{ fontWeight: "bold", letterSpacing: "1px" }}>CASE_STUDY_03</span>
        </div>
        <div style={navLinks}>
          <button onClick={() => navigate({ to: "/Portfolio" })} style={backButtonStyle}>
            ← BACK_TO_PORTFOLIO
          </button>
        </div>
      </nav>

      <main style={mainContent}>
        <header style={headerSection}>
          <div style={labelStyle}>DATA_ENGINEERING_&_AUTOMATION</div>
          <h1 style={titleStyle}>Multi-Source E-commerce Aggregator</h1>
          <p style={subtitleStyle}>Automating the aggregation of YouTube metadata and SKU manifests into normalized e-commerce templates.</p>
          <div style={tagRow}>
            {["PYTHON", "YOUTUBE_SCRAPING", "OPENPYXL", "PANDAS", "ETL", "E-COMMERCE"].map((t) => (
              <span key={t} style={tagStyle}>{t}</span>
            ))}
          </div>
        </header>

        <section style={sectionBox}>
          <span style={sectionLabel}>01. PROJECT_SCOPE</span>
          <div style={gridTwo}>
            <div>
              <h3 style={smallHeading}>THE BOTTLENECK</h3>
              <p style={paragraph}>
                Marketing teams were manually scouring YouTube videos for titles and links, then cross-referencing them with inventory SKUs spread across multiple spreadsheets. This manual "copy-paste" workflow was a significant time sink and prone to critical formatting errors.
              </p>
            </div>
            <div>
              <h3 style={smallHeading}>THE AUTOMATION</h3>
              <p style={paragraph}>
                I developed a **Python-based ETL engine** that programmatically scrapes YouTube metadata and joins it with SKU data from varied Excel sources. The script performs complex data normalization to fit a **custom e-commerce template**, generating SEO-friendly titles and tracking links automatically.
              </p>
            </div>
          </div>
        </section>

        <section style={sectionBox}>
          <span style={sectionLabel}>02. AGGREGATION_SANDBOX</span>
          <div style={webWindow}>
            <div style={webTabRow}>
              {(['AGGREGATION_ENGINE', 'DATA_NORMALIZATION', 'SOURCE_HEALTH'] as Tab[]).map(t => (
                <span key={t} onClick={() => setActiveTab(t)}
                  style={{ ...webTab, color: activeTab === t ? '#3b82f6' : '#555', borderBottom: activeTab === t ? '2px solid #3b82f6' : 'none' }}>
                  {t}
                </span>
              ))}
            </div>

            <div style={apiGridContainer}>
              <div style={{ minHeight: '300px' }}>
                {activeTab === 'AGGREGATION_ENGINE' && (
                  <div>
                    <div style={metricGrid}>
                      <div style={metricCard}>
                        <h2 style={metricValue}>{itemsAggregated.toLocaleString()}</h2>
                        <span style={metricLabel}>NORMALIZED_PRODUCTS</span>
                      </div>
                      <div style={metricCard}>
                        <h2 style={{ ...metricValue, color: '#3b82f6' }}>{progress}%</h2>
                        <span style={metricLabel}>BATCH_PROGRESS</span>
                      </div>
                    </div>
                    <div style={healthBar}>
                      <div style={{ ...healthFill, width: `${progress}%`, backgroundColor: '#3b82f6' }}></div>
                    </div>
                    <button style={webButton} onClick={triggerAutomation} disabled={isProcessing}>
                      {isProcessing ? 'RUNNING_ETL_PIPELINE...' : 'START_DATA_AGGREGATION'}
                    </button>
                  </div>
                )}

                {activeTab === 'DATA_NORMALIZATION' && (
                  <div style={webLogTerminal}>
                    {logs.map((log, i) => (
                      <div key={i} style={{ 
                        color: log.includes('SUCCESS') ? '#10b981' : log.includes('PY') ? '#f59e0b' : '#3b82f6', 
                        marginBottom: '4px', fontSize: '11px'
                      }}>
                        {log}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'SOURCE_HEALTH' && (
                  <table style={tableStyle}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#444' }}>
                        <th style={thStyle}>SOURCE_NAME</th>
                        <th style={thStyle}>TYPE</th>
                        <th style={thStyle}>STATUS</th>
                        <th style={thStyle}>PAYLOAD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.map(source => (
                        <tr key={source.name} style={{ borderBottom: '1px solid #1a1a1a' }}>
                          <td style={tdStyle}>{source.name}</td>
                          <td style={{ ...tdStyle, color: '#666' }}>{source.type}</td>
                          <td style={{ ...tdStyle, color: source.status === 'CONNECTED' || source.status === 'STABLE' ? '#10b981' : '#f59e0b' }}>{source.status}</td>
                          <td style={tdStyle}>{source.items}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div style={infoSidebar}>
                <div style={sideLabel}>TEMPLATE_ACCURACY</div>
                <div style={healthBar}><div style={{ ...healthFill, width: '100%', backgroundColor: '#10b981' }}></div></div>
                <div style={{ fontSize: '10px', color: '#555', marginTop: '5px' }}>PASSED_VALIDATION</div>
                
                <div style={{ marginTop: '30px' }}>
                  <div style={sideLabel}>CORE_DEPENDENCIES</div>
                  <div style={{ color: '#fff', fontSize: '11px' }}>google-api-python-client</div>
                  <div style={{ color: '#fff', fontSize: '11px', marginTop: '4px' }}>OpenPyXL (XLSX Writer)</div>
                  <div style={{ color: '#fff', fontSize: '11px', marginTop: '4px' }}>Pandas (Join/Merge)</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        

        <section style={sectionBox}>
          <span style={sectionLabel}>03. STRATEGIC_IMPACT</span>
          <div style={gridThree}>
            <div style={cardStyle}>
              <div style={cardNum}>01</div>
              <h4 style={cardTitle}>OPERATIONAL_EFFICIENCY</h4>
              <p style={cardText}>Reclaimed hundreds of hours for the marketing team by automating the manual cross-referencing of video content and inventory data.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>02</div>
              <h4 style={cardTitle}>DATA_CONSISTENCY</h4>
              <p style={cardText}>Standardized titles and URL patterns across the entire site, ensuring the e-commerce platform remained clean and professional.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>03</div>
              <h4 style={cardTitle}>SCALABILITY</h4>
              <p style={cardText}>The Python script can process 1,000+ entries as easily as one, allowing the marketing team to expand their content strategy without increasing headcount.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* STYLES */
const containerStyle: React.CSSProperties = { backgroundColor: "#0a0a0a", color: "#e0e0e0", minHeight: "100vh", padding: "120px 24px 60px 24px", fontFamily: "monospace" };
const navStyle: React.CSSProperties = { position: "fixed", top: 0, left: 0, right: 0, height: "70px", backgroundColor: "rgba(10, 10, 10, 0.8)", backdropFilter: "blur(12px)", borderBottom: "1px solid #222", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 40px", zIndex: 1000 };
const navBrand: React.CSSProperties = { display: "flex", alignItems: "center", gap: "12px" };
const avatarStyle: React.CSSProperties = { width: "32px", height: "32px", backgroundColor: "#1a1a1a", border: "1px solid #333", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "12px", fontWeight: "bold", borderRadius: "8px" };
const navLinks: React.CSSProperties = { display: "flex", gap: "30px" };
const backButtonStyle: React.CSSProperties = { backgroundColor: "transparent", border: "1px solid #333", color: "#666", padding: "8px 16px", fontSize: "11px", fontWeight: "bold", cursor: "pointer" };
const mainContent: React.CSSProperties = { maxWidth: "1000px", margin: "0 auto" };
const headerSection: React.CSSProperties = { marginBottom: "60px", borderBottom: "1px solid #1a1a1a", paddingBottom: "40px" };
const titleStyle: React.CSSProperties = { fontSize: "32px", color: "#fff", margin: "10px 0", fontWeight: "bold" };
const subtitleStyle: React.CSSProperties = { fontSize: "16px", color: "#3b82f6", marginBottom: "25px", maxWidth: "600px" };
const labelStyle: React.CSSProperties = { fontSize: "10px", color: "#666", marginBottom: "12px", letterSpacing: "1px" };
const tagRow: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const tagStyle: React.CSSProperties = { fontSize: "10px", backgroundColor: "#1a1a1a", border: "1px solid #333", padding: "4px 12px", color: "#aaa", borderRadius: "6px" };
const sectionBox: React.CSSProperties = { marginBottom: "100px" };
const sectionLabel: React.CSSProperties = { fontSize: "10px", color: "#444", letterSpacing: "2px", display: "block", marginBottom: "30px" };
const gridTwo: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px" };
const smallHeading: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold" };
const paragraph: React.CSSProperties = { fontSize: "14px", color: "#888", lineHeight: "1.6", margin: 0 };

const webWindow: React.CSSProperties = { backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px', padding: '30px' };
const webTabRow: React.CSSProperties = { display: 'flex', gap: '20px', borderBottom: '1px solid #222', marginBottom: '30px' };
const webTab: React.CSSProperties = { fontSize: '10px', cursor: 'pointer', paddingBottom: '12px', fontWeight: 'bold', letterSpacing: '1px' };
const apiGridContainer: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 300px', gap: '30px' };
const metricGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' };
const metricCard: React.CSSProperties = { backgroundColor: '#0a0a0a', border: '1px solid #222', padding: '20px', borderRadius: '12px' };
const metricValue: React.CSSProperties = { fontSize: "28px", color: "#fff", margin: 0 };
const metricLabel: React.CSSProperties = { fontSize: "9px", color: "#444", marginTop: '5px', display: 'block' };
const webLogTerminal: React.CSSProperties = { backgroundColor: '#050505', padding: '15px', borderRadius: '8px', fontSize: '11px', height: '200px', overflowY: 'auto' };
const webButton: React.CSSProperties = { width: '100%', backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: '14px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer', marginTop: '20px' };

const infoSidebar: React.CSSProperties = { borderLeft: '1px solid #222', paddingLeft: '30px' };
const sideLabel: React.CSSProperties = { fontSize: '9px', color: '#444', marginBottom: '10px' };
const healthBar: React.CSSProperties = { width: '100%', height: '4px', backgroundColor: '#222', borderRadius: '2px', overflow: 'hidden' };
const healthFill: React.CSSProperties = { height: '100%', backgroundColor: '#10b981', transition: 'width 0.4s ease' };

const gridThree: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" };
const cardStyle: React.CSSProperties = { backgroundColor: "#111", padding: "30px", borderRadius: "16px", border: "1px solid #222", position: "relative" };
const cardNum: React.CSSProperties = { fontSize: "40px", color: "#1a1a1a", fontWeight: "bold", position: "absolute", top: "10px", right: "20px" };
const cardTitle: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold" };
const cardText: React.CSSProperties = { fontSize: "13px", color: "#888", lineHeight: "1.6" };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '11px' };
const thStyle: React.CSSProperties = { paddingBottom: '10px', fontWeight: 'normal', borderBottom: '1px solid #222' };
const tdStyle: React.CSSProperties = { padding: '12px 0', color: '#fff' };

export default InfraAutomation;